import {
    Box, IconButton, Typography, Menu, MenuItem, ListItemIcon, ListItemText, Divider,
} from "@mui/material";
import {
    ArrowForward,
    ManageAccounts as ManageAccountsIcon,
    EditOutlined,
    Lock as LockIcon,
    ToggleOn,
    ToggleOff,
    DeleteOutline,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Cake as CakeIcon,
    Wc as GenderIcon,
    Star as DefaultTrainerIcon,
    Groups as GroupsIcon,
} from "@mui/icons-material";
import {useEffect, useState} from "react";
import {deleteTrainer, disableTrainer, enableTrainer, getTrainer, makeDefaultTrainer, updateTrainer} from "../services/TrainerService";
import {useNavigate, useParams} from "react-router-dom";
import TrainerProfileSection from "../componenets/trainers/TrainerProfileSection";
import NewTrainerDialog from "../componenets/NewTrainerDialog";
import {ConfirmDialog, StatusChip} from "../componenets/ui";
import ChangePasswordDialog from "../componenets/client/ChangePasswordDialog";
import {getErrorMessage} from "../enums/ErrorMessages";
import {useMessageContext} from "../contexts/MessageContext";
import {useAuth} from "../contexts/AuthContext";
import {t as coachT} from "../componenets/dashboard/coach/coachTokens";
import {formatPhoneForDisplay} from "../componenets/shared/PhoneInput";
import {ReactComponent as ActiveSvg} from "../assets/images/active.svg";
import {ReactComponent as InactiveSvg} from "../assets/images/inactive.svg";

/* ── Detail cell (one field inside the profile card grid) ── */
const DetailCell = ({icon: Icon, label, value}) => (
    <Box sx={{display: "flex", alignItems: "center", gap: 1.25, minWidth: 0}}>
        <Box sx={{
            width: 34, height: 34, borderRadius: "10px", flexShrink: 0,
            background: coachT.bg, color: coachT.t3,
            display: "flex", alignItems: "center", justifyContent: "center",
        }}>
            <Icon sx={{fontSize: 18}}/>
        </Box>
        <Box sx={{display: "flex", flexDirection: "column", lineHeight: 1.2, minWidth: 0}}>
            <Typography sx={{fontSize: "10.5px", fontWeight: 600, color: coachT.t3, letterSpacing: ".02em"}}>{label}</Typography>
            <Typography sx={{fontSize: "13px", fontWeight: 700, color: coachT.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{value}</Typography>
        </Box>
    </Box>
);

const TrainerPage = () => {
    const params = useParams();
    const navigate = useNavigate();
    const {notificationHandler} = useMessageContext();
    const {isSuperAdmin, isCompanyAdmin} = useAuth();

    const [trainer, setTrainer] = useState();
    const [editTrainer, setEditTrainer] = useState(false);
    const [reload, setReload] = useState(false);

    const [menuAnchor, setMenuAnchor] = useState(null);
    const menuOpen = Boolean(menuAnchor);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [changePasswordOpen, setChangePasswordOpen] = useState(false);

    useEffect(() => {
        getTrainer(params.trainerId)
            .then(r => setTrainer(r.data))
            .catch(e => notificationHandler.error(getErrorMessage(e.response.data?.errorCode)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reload, params.trainerId]);

    const handleDeleteTrainer = () => {
        deleteTrainer(params.trainerId)
            .then(() => navigate('/app/trainers'))
            .then(() => notificationHandler.success("מאמן נמחק בהצלחה"))
            .catch(e => notificationHandler.error(getErrorMessage(e.response.data?.errorCode)));
    };

    const handleDisableEnableTrainer = () => {
        const action = trainer.enabled ? disableTrainer : enableTrainer;
        const msg = trainer.enabled ? "מאמן בוטל בהצלחה" : "מאמן הופעל בהצלחה";
        action(trainer.id)
            .then(() => setReload(prv => !prv))
            .then(() => notificationHandler.success(msg))
            .catch(e => notificationHandler.error(getErrorMessage(e.response.data?.errorCode)));
    };

    const handleMakeDefaultTrainer = () => {
        makeDefaultTrainer(trainer.id)
            .then(() => setReload(prv => !prv))
            .then(() => notificationHandler.success("מאמן הוגדר כברירת מחדל בהצלחה"))
            .catch(e => notificationHandler.error(getErrorMessage(e.response.data?.errorCode)));
    };

    const handleChangePassword = (password) => {
        updateTrainer({id: trainer.id, password})
            .then(() => { setChangePasswordOpen(false); notificationHandler.success("סיסמה שונתה בהצלחה"); })
            .catch(e => notificationHandler.error(getErrorMessage(e.response.data?.errorCode)));
    };

    const genderText = trainer?.gender === "MALE" ? "זכר" : trainer?.gender === "FEMALE" ? "נקבה" : null;

    const menuItems = [
        {label: "ערוך פרופיל", icon: <EditOutlined fontSize="small"/>, onClick: () => { setEditTrainer(true); setMenuAnchor(null); }},
        {label: "שנה סיסמה", icon: <LockIcon fontSize="small"/>, onClick: () => { setChangePasswordOpen(true); setMenuAnchor(null); }},
        ...((isSuperAdmin() || isCompanyAdmin) ? [
            {divider: true},
            {
                label: trainer?.enabled ? "חסום מאמן" : "הפעל מאמן",
                icon: trainer?.enabled ? <ToggleOff fontSize="small"/> : <ToggleOn fontSize="small"/>,
                onClick: () => { handleDisableEnableTrainer(); setMenuAnchor(null); },
                color: trainer?.enabled ? coachT.red : coachT.green,
            },
            {
                label: trainer?.defaultTrainer ? "מאמן ברירת מחדל" : "הגדר כברירת מחדל",
                icon: <DefaultTrainerIcon fontSize="small"/>,
                onClick: () => { handleMakeDefaultTrainer(); setMenuAnchor(null); },
                disabled: trainer?.defaultTrainer,
            },
            {divider: true},
            {
                label: "מחק מאמן",
                icon: <DeleteOutline fontSize="small"/>,
                onClick: () => { setDeleteOpen(true); setMenuAnchor(null); },
                color: coachT.red,
            },
        ] : []),
    ];

    return (
        <Box sx={{display: "flex", flexDirection: "column", gap: "18px"}}>

            {/* ─── Header ──────────────────────────────────── */}
            <Box sx={{display: "flex", flexDirection: "column", gap: 1.25, flexShrink: 0}}>
                {/* Top action bar */}
                <Box sx={{display: "flex", alignItems: "center", gap: 1.25}}>
                    <IconButton
                        onClick={() => navigate('/app/trainers')}
                        sx={{color: coachT.t2, p: 1, "&:hover": {bgcolor: coachT.greenLight, color: coachT.green}}}
                    >
                        <ArrowForward/>
                    </IconButton>
                    <Typography sx={{fontSize: "13px", fontWeight: 600, color: coachT.t2}}>חזרה למאמנים</Typography>
                </Box>

                {/* Profile header card */}
                {trainer && (
                    <Box sx={{
                        background: coachT.white, border: `1px solid ${coachT.border}`,
                        borderRadius: coachT.r, boxShadow: coachT.sh, overflow: "hidden",
                    }}>
                        {/* Identity row */}
                        <Box sx={{
                            display: "flex", alignItems: "center", gap: 1.25,
                            padding: {xs: "14px 16px", md: "16px 22px"},
                            borderBottom: `1px solid ${coachT.border}`,
                            background: `linear-gradient(135deg, ${coachT.greenLight}, #f4fbf8)`,
                        }}>
                            <Box sx={{minWidth: 0, display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap"}}>
                                <Typography sx={{fontSize: {xs: "21px", md: "25px"}, fontWeight: 800, color: coachT.text, lineHeight: 1.2, letterSpacing: "-0.3px"}}>
                                    {trainer.name || "פרטי מאמן"}
                                </Typography>
                                {trainer.enabled
                                    ? <StatusChip label="פעיל" variant="success" icon={<ActiveSvg style={{width: 11, height: 11}}/>} size="small"/>
                                    : <StatusChip label="חסום" variant="error" icon={<InactiveSvg style={{width: 11, height: 11}}/>} size="small"/>}
                                {trainer.defaultTrainer && (
                                    <StatusChip label="מאמן ברירת מחדל" variant="info" size="small"/>
                                )}
                            </Box>

                            <Box sx={{flex: 1}}/>

                            {/* 3-dot ActionMenu */}
                            <IconButton
                                onClick={e => setMenuAnchor(e.currentTarget)}
                                sx={{
                                    color: coachT.t2, border: `1px solid ${coachT.border}`, background: coachT.white,
                                    borderRadius: coachT.rs, width: 40, height: 40, flexShrink: 0,
                                    "&:hover": {bgcolor: coachT.greenLight, borderColor: coachT.green, color: coachT.green},
                                }}
                            >
                                <ManageAccountsIcon/>
                            </IconButton>
                            <Menu
                                anchorEl={menuAnchor} open={menuOpen}
                                onClose={() => setMenuAnchor(null)}
                                anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                                transformOrigin={{vertical: "top", horizontal: "left"}}
                                PaperProps={{sx: {
                                    borderRadius: coachT.rs, border: `1px solid ${coachT.border}`,
                                    boxShadow: coachT.sh, mt: 0.5, minWidth: 200,
                                }}}
                            >
                                {menuItems.map((item, i) =>
                                    item.divider ? (
                                        <Divider key={`d-${i}`} sx={{my: 0.5}}/>
                                    ) : (
                                        <MenuItem
                                            key={item.label}
                                            onClick={item.onClick}
                                            disabled={item.disabled}
                                            sx={{
                                                fontSize: "14px", fontWeight: 500, py: 1.25, px: 2,
                                                mx: 0.75, borderRadius: coachT.rxs,
                                                color: item.color || coachT.text,
                                                opacity: item.disabled ? 0.5 : 1,
                                                "&:hover": {
                                                    bgcolor: item.color ? `${item.color}14` : coachT.greenLight,
                                                    color: item.color || coachT.green,
                                                },
                                            }}
                                        >
                                            <ListItemIcon sx={{color: "inherit", minWidth: 32}}>{item.icon}</ListItemIcon>
                                            <ListItemText primaryTypographyProps={{fontSize: "14px", fontWeight: 500}}>{item.label}</ListItemText>
                                        </MenuItem>
                                    )
                                )}
                            </Menu>
                        </Box>

                        {/* Details grid */}
                        <Box sx={{
                            display: "grid",
                            gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)"},
                            gap: {xs: 1.75, md: 2.25},
                            padding: {xs: "16px", md: "18px 22px"},
                        }}>
                            <DetailCell icon={GroupsIcon} label="מספר לקוחות" value={(trainer.numberOfClients ?? 0).toLocaleString()}/>
                            {trainer.email && <DetailCell icon={EmailIcon} label="מייל" value={trainer.email}/>}
                            {trainer.phoneNumber && <DetailCell icon={PhoneIcon} label="טלפון" value={formatPhoneForDisplay(trainer.phoneNumber)}/>}
                            {trainer.age && <DetailCell icon={CakeIcon} label="גיל" value={trainer.age}/>}
                            {genderText && <DetailCell icon={GenderIcon} label="מגדר" value={genderText}/>}
                        </Box>
                    </Box>
                )}
            </Box>

            {/* ─── Content ─────────────────────────────────── */}
            {trainer && (
                <TrainerProfileSection trainer={trainer}/>
            )}

            {/* ─── Dialogs ─────────────────────────────────── */}
            {trainer && (
                <>
                    <NewTrainerDialog reload={setReload} trainer={trainer} open={editTrainer} close={() => setEditTrainer(false)}/>
                    <ConfirmDialog
                        open={deleteOpen}
                        onClose={() => setDeleteOpen(false)}
                        onConfirm={handleDeleteTrainer}
                        title="מחיקת מאמן"
                        description={`האם למחוק את ${trainer.name}? פעולה זו אינה ניתנת לביטול.`}
                        confirmText="מחק"
                        variant="danger"
                    />
                    <ChangePasswordDialog
                        isOpen={changePasswordOpen}
                        close={() => setChangePasswordOpen(false)}
                        onSubmit={handleChangePassword}
                    />
                </>
            )}
        </Box>
    );
};

export default TrainerPage;
